import { useEffect, useRef } from 'react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { ITrackingContext, MixPanelEventEmitter } from './MixPanelEventEmitter';
import { FullScreenOverlayState } from '../support/FullScreenOverlayState';

const DEFAULT_MIN_VISIBLE_MS = 750;

/**
 * Mixpanel.track replacement for "page/tab opened" style events. Fires the
 * event only after the calling component has been visible for at least
 * `minVisibleMs` with no full-screen overlay covering it (welcome slides,
 * intro screen, etc.). The check restarts if an overlay opens during the
 * wait period, so a user who lands on a covered page never produces a
 * misleading "opened" event.
 *
 * Fires at most once per component lifetime; remounting (e.g. navigating
 * back to the page) resets it.
 */
export const useTrackOnVisible = (
    event: string,
    getContext?: () => ITrackingContext,
    minVisibleMs: number = DEFAULT_MIN_VISIBLE_MS
) => {
    const mixpanel = useInstance(MixPanelEventEmitter);
    const overlayState = useInstance(FullScreenOverlayState);
    useSubscription(() => overlayState);

    const firedRef = useRef(false);
    // Hold event/getContext in refs so the timer callback always reads the
    // latest values. The effect intentionally only re-runs on overlay-count
    // changes, so without refs a caller that passes a dynamic event would
    // emit the stale value captured at the first qualifying run.
    const eventRef = useRef(event);
    const getContextRef = useRef(getContext);
    eventRef.current = event;
    getContextRef.current = getContext;

    useEffect(() => {
        if (firedRef.current) {
            return;
        }
        if (overlayState.hasActiveOverlay()) {
            return;
        }

        const timer = setTimeout(() => {
            // Re-check at fire time: an overlay could have opened between the
            // re-render that scheduled this timer and now.
            if (firedRef.current) {
                return;
            }
            if (overlayState.hasActiveOverlay()) {
                return;
            }
            firedRef.current = true;
            mixpanel.track(eventRef.current, getContextRef.current);
        }, minVisibleMs);

        return () => clearTimeout(timer);
    // We intentionally re-run only when the overlay count changes; event and
    // getContext are read from refs at fire time so dynamic values are safe.
    }, [overlayState.state.activeOverlayIds.length]); // eslint-disable-line react-hooks/exhaustive-deps
};
